import { gsap } from "gsap";
import { brand as defaultBrand } from "../config/brand.js";

export class AnimationController {
  constructor(scene3D, camera, brandConfig = defaultBrand) {
    this.scene3D = scene3D;
    this.camera = camera;
    this.brand = brandConfig;
    this.mainObject = scene3D.getMainObject();
    this.wireframeObject = scene3D.getWireframeObject();
    this.particles = scene3D.getParticles();

    this.targetRotationX = 0;
    this.targetRotationY = 0;
    this.currentRotationX = 0;
    this.currentRotationY = 0;
    this.baseRotationY = 0;
    this.lastTime = null;
    this.cameraTarget = { x: 0, y: 0 };
    this.scrollProgress = 0;
    this.currentScrollProgress = 0;
    this.isAnimating = false;
    this.materialsToggled = false;

    this.setupInitialAnimations();
  }

  setupInitialAnimations() {
    const { animation } = this.brand;

    // Fade in main object
    gsap.from(this.mainObject.scale, {
      x: 0,
      y: 0,
      z: 0,
      duration: animation.introDuration,
      ease: animation.introEase,
    });

    gsap.from(this.wireframeObject.scale, {
      x: 0,
      y: 0,
      z: 0,
      duration: animation.introDuration,
      delay: 0.2,
      ease: animation.introEase,
    });

    gsap.from(this.particles.material, {
      opacity: 0,
      duration: animation.introDuration,
      delay: 0.35,
      ease: "power2.out",
    });
  }

  handleMouseMove(x, y) {
    this.targetRotationX = y * this.brand.animation.mouseInfluence;
    this.targetRotationY = x * this.brand.animation.mouseInfluence;
  }

  handleScroll(progress) {
    this.scrollProgress = Math.min(Math.max(progress, 0), 1);
  }

  applyBrandConfig(brandConfig) {
    this.brand = brandConfig;
  }

  update() {
    const delta = this.getDeltaTime();
    const { animation, scene } = this.brand;

    // Smooth rotation interpolation
    this.currentRotationX +=
      (this.targetRotationX - this.currentRotationX) * animation.rotationLerp;
    this.currentRotationY +=
      (this.targetRotationY - this.currentRotationY) * animation.rotationLerp;
    this.baseRotationY += animation.rotationSpeed * delta;
    this.currentScrollProgress +=
      (this.scrollProgress - this.currentScrollProgress) * animation.scrollLerp;

    const scroll = this.currentScrollProgress;

    this.mainObject.rotation.x =
      this.currentRotationX + scroll * animation.scrollTilt;
    this.mainObject.rotation.y =
      this.currentRotationY +
      this.baseRotationY +
      scroll * animation.scrollRotation;
    this.wireframeObject.rotation.x = this.mainObject.rotation.x;
    this.wireframeObject.rotation.y = this.mainObject.rotation.y;

    // Camera movement based on mouse
    this.cameraTarget.x +=
      (this.targetRotationY * animation.cameraFollowRange -
        this.cameraTarget.x) *
      animation.cameraLerp;
    this.cameraTarget.y +=
      (this.targetRotationX * animation.cameraFollowRange -
        this.cameraTarget.y) *
      animation.cameraLerp;
    this.camera.position.x = this.cameraTarget.x;
    this.camera.position.y = this.cameraTarget.y + scroll * 0.65;
    this.camera.position.z =
      scene.camera.position[2] - scroll * animation.scrollCameraDepth;
    this.camera.lookAt(0, 0, 0);

    // Animate particles
    const time = this.getNow();
    this.particles.position.y =
      Math.sin(time) * scene.particles.floatAmplitude +
      scroll * animation.scrollObjectLift;
    this.particles.rotation.y += scene.particles.rotationSpeed * delta;
  }

  getNow() {
    if (globalThis.performance) return globalThis.performance.now() * 0.001;
    return Date.now() * 0.001;
  }

  getDeltaTime() {
    const now = this.getNow();
    const delta =
      this.lastTime === null ? 0 : Math.min(now - this.lastTime, 0.05);
    this.lastTime = now;
    return delta;
  }

  triggerAnimation() {
    if (this.isAnimating) return;

    this.isAnimating = true;
    const { animation } = this.brand;

    // Jump animation
    const tl = gsap.timeline({
      onComplete: () => {
        this.isAnimating = false;
      },
    });

    tl.to(this.mainObject.position, {
      y: animation.jumpHeight,
      duration: 0.5,
      ease: "power2.inOut",
    }).to(
      this.mainObject.position,
      {
        y: 0,
        duration: 0.5,
        ease: "elastic.out(1, 0.5)",
      },
      "-=0.3",
    );

    // Scale animation
    gsap.to(this.mainObject.scale, {
      x: animation.jumpScale,
      y: animation.jumpScale,
      z: animation.jumpScale,
      duration: 0.3,
      yoyo: true,
      repeat: 1,
    });
  }

  resetAnimation() {
    const { camera, wireframe } = this.brand.scene;

    gsap.killTweensOf(this.mainObject.position);
    gsap.killTweensOf(this.mainObject.rotation);
    gsap.killTweensOf(this.mainObject.scale);
    gsap.killTweensOf(this.wireframeObject.scale);
    gsap.killTweensOf(this.wireframeObject.material);
    gsap.killTweensOf(this.particles.material);

    this.mainObject.position.set(0, 0, 0);
    this.mainObject.rotation.set(0, 0, 0);
    this.mainObject.scale.set(1, 1, 1);
    this.mainObject.material.wireframe = false;

    this.wireframeObject.material.opacity = wireframe.opacity;
    this.camera.position.set(...camera.position);
    this.cameraTarget = { x: 0, y: 0 };
    this.targetRotationX = 0;
    this.targetRotationY = 0;
    this.currentRotationX = 0;
    this.currentRotationY = 0;
    this.baseRotationY = 0;
    this.lastTime = null;
    this.scrollProgress = 0;
    this.currentScrollProgress = 0;
    this.materialsToggled = false;

    this.setupInitialAnimations();
  }

  toggleMaterials() {
    const { wireframe } = this.brand.scene;
    const nextWireframe = !this.materialsToggled;

    gsap.to(this.wireframeObject.material, {
      opacity: nextWireframe ? wireframe.activeOpacity : wireframe.opacity,
      duration: 0.3,
    });

    this.mainObject.material.wireframe = nextWireframe;

    this.materialsToggled = nextWireframe;
  }

  dispose() {
    gsap.killTweensOf(this.mainObject.position);
    gsap.killTweensOf(this.mainObject.rotation);
    gsap.killTweensOf(this.mainObject.scale);
    gsap.killTweensOf(this.wireframeObject.scale);
    gsap.killTweensOf(this.wireframeObject.material);
    gsap.killTweensOf(this.particles.material);
    gsap.killTweensOf(this.camera.position);
  }
}
